import { action, computed, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { EMPTY, distinctUntilChanged, filter, switchMap } from 'rxjs'
import type { FetchBillingProfileFn } from '@/features/billing'
import { type BillingMethod } from '@/features/billing/profile/BillingMethod'
import type { DocumentsTabViewModelFactory } from '@/features/documents'
import type {
  AdvisorClientsTabViewModelFactory,
  ClientAssignmentViewModelFactory,
} from '@/features/client-user-management'
import type {
  FetchSearchableUserFn,
  GetCurrentMemberFn,
  GetCurrentUserWorkspaceMembershipsFn,
  PrimeProviderFn,
} from '@/features/iam'
import { Provider } from '@/features/iam/providers/Provider'
import { SearchableUser } from '@/features/iam/users/SearchableUser'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import type {
  FetchJobTotalsFn,
  PromptCreateJobFn,
  UserJobsTabViewModelFactory,
} from '@/features/jobs'
import type { FetchPayoutMethodInfoFn } from '@/features/payouts'
import { type PayoutMethodInfo } from '@/features/payouts/admin/AdminPayoutMethodStore'
import { Id } from '@/infra/id'
import { activation$, type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { toStream } from '@/utils/mobx-util'
import { UserInfoItemListDialogViewModel } from '@/features/user-management/UserInfoItemListDialog/UserInfoItemListDialogViewModel'
import { type AddUserToTeamDialogViewModel } from '@/features/user-management/AddUserToTeamDialog/AddUserToTeamDialogViewModel'
import { type AddUserToWorkspaceDialogViewModel } from '@/features/user-management/AddUserToWorkspaceDialog/AddUserToWorkspaceDialogViewModel'
import { type AddCustomerToWorkspaceDialogViewModel } from '@/features/user-management/AddCustomerToWorkspaceDialog/AddCustomerToWorkspaceDialogViewModel'
import { type JoinWorkspaceAsAdminDialogViewModel } from '@/features/user-management/JoinWorkspaceAsAdminDialog/JoinWorkspaceAsAdminDialogViewModel'
import { type EditUserInformationDialogViewModel } from '@/features/user-management/EditUserInformationDialog/EditUserInformationDialogViewModel'
import { type EditProDetailsDialogViewModel } from '@/features/user-management/EditProDetailsDialog/EditProDetailsDialogViewModel'
import { type EditUserRoleDialogViewModel } from '@/features/user-management/EditUserRoleDialog/EditUserRoleDialogViewModel'
import { type ManageSkillsListDialogViewModel } from '@/features/user-management/ManageSkillsListDialog/ManageSkillsListDialogViewModel'
import { type ManageUserCapacityDialogViewModel } from '@/features/user-management/ManageUserCapacityDialog/ManageUserCapacityDialogViewModel'
import {
  type MembershipsTabDialogs,
  type MembershipsTabViewModel,
} from '@/features/user-management/MembershipsTab/MembershipsTabViewModel'
import { type RemoveFromWorkspaceDialogViewModel } from '@/features/user-management/RemoveFromWorkspaceDialog/RemoveFromWorkspaceDialogViewModel'
import { type RestoreToWorkspaceDialogViewModel } from '@/features/user-management/RestoreToWorkspaceDialog/RestoreToWorkspaceDialogViewModel'
import { type PromoteToOwnerDialogViewModel } from '@/features/user-management/PromoteToOwnerDialog/PromoteToOwnerDialogViewModel'

/**
 * List of tabs to be displayed in the user details view.
 */
export const userDetailsTabs = [
  'Jobs',
  'Memberships',
  'Documents',
  'Clients',
] as const

export type UserDetailsTab = (typeof userDetailsTabs)[number]

type CreateRemoveFromWorkspaceDialogViewModelFn = (
  onRemoved: () => void,
) => RemoveFromWorkspaceDialogViewModel

type CreateManageUserCapacityDialogViewModelFn = (
  onUpdated: () => void,
) => ManageUserCapacityDialogViewModel

type CreatePromoteToOwnerDialogViewModelFn = (
  onPromoted: () => Promise<void>,
) => PromoteToOwnerDialogViewModel

type CreateAddUserToTeamDialogViewModelFn = (
  user: () => SearchableUser,
) => AddUserToTeamDialogViewModel

type CreateAddUserToWorkspaceDialogViewModelFn = (
  user: () => SearchableUser,
  onAdded: () => Promise<void>,
) => AddUserToWorkspaceDialogViewModel

type CreateAddCustomerToWorkspaceDialogViewModelFn = (
  user: () => SearchableUser,
  onAdded: () => Promise<void>,
) => AddCustomerToWorkspaceDialogViewModel

type CreateJoinWorkspaceAsAdminDialogViewModelFn = (
  userMembershipWorkspaceIds: () => Id[],
  onJoined: () => Promise<void>,
) => JoinWorkspaceAsAdminDialogViewModel

type CreateEditUserInformationDialogViewModelFn = (
  onSaved: () => Promise<void>,
) => EditUserInformationDialogViewModel

type CreateMembershipsTabViewModelFn = (
  user: () => SearchableUser,
  dialogs: MembershipsTabDialogs,
  canJoinWorkspaceAsAdmin: () => boolean,
  canPromoteToOwner: () => boolean,
) => MembershipsTabViewModel

export class UserDetailsViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The currently selected user.
   */
  @observable
  accessor user: SearchableUser = SearchableUser.none

  /**
   * Provider data for the user if user is a provider.
   */
  @observable
  accessor provider: Provider = Provider.none

  /**
   * List of payment methods for the currently selected user.
   */
  @observable
  accessor paymentMethods: BillingMethod[] = []

  /**
   * The payment method for the Pro, if any.
   */
  @observable
  accessor payoutMethodInfo: PayoutMethodInfo = { status: 'Unknown' }

  /**
   * The currently selected tab.
   */
  @observable
  accessor tab: UserDetailsTab = 'Jobs'

  /**
   * Number of active jobs for the currently selected user.
   */
  @observable
  accessor activeJobs = 0

  /**
   * Number of total jobs for the currently selected user.
   */
  @observable
  accessor totalJobs = 0

  readonly promoteToOwnerVm: PromoteToOwnerDialogViewModel
  readonly removeFromWorkspaceVm: RemoveFromWorkspaceDialogViewModel
  readonly addUserToTeamVm: AddUserToTeamDialogViewModel
  readonly addUserToWorkspaceVm: AddUserToWorkspaceDialogViewModel
  readonly addCustomerToWorkspaceVm: AddCustomerToWorkspaceDialogViewModel
  readonly joinWorkspaceAsAdminVm: JoinWorkspaceAsAdminDialogViewModel
  readonly userInfoItemListVm = new UserInfoItemListDialogViewModel()
  readonly editUserInformationVm: EditUserInformationDialogViewModel
  readonly manageUserCapacityVm: ManageUserCapacityDialogViewModel
  readonly membershipsTabVm: MembershipsTabViewModel
  readonly jobsTabVm: ReturnType<UserJobsTabViewModelFactory>
  readonly documentsTabVm: ReturnType<DocumentsTabViewModelFactory>

  /**
   * Clients tab view model, shown when the user is an advisor.
   */
  readonly clientsTabVm: ReturnType<AdvisorClientsTabViewModelFactory>

  /**
   * The user's advisors, or whether the user is an advisor.
   */
  readonly clientAssignmentVm: ReturnType<ClientAssignmentViewModelFactory>

  /**
   * Loads the user that the page shows.
   */
  fetch = task(async (signal: AbortSignal) => {
    const user = await this._fetchUser(this.userPublicId, signal)
    runInAction(() => {
      this.user = user
    })
  })

  constructor(
    readonly userPublicId: Id,
    private readonly _fetchSearchableUser: FetchSearchableUserFn,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    private readonly _primeProvider: PrimeProviderFn,
    private readonly _fetchBillingProfile: FetchBillingProfileFn,
    private readonly _fetchPayoutMethodInfo: FetchPayoutMethodInfoFn,
    private readonly _fetchJobTotals: FetchJobTotalsFn,
    private readonly _promptCreateJob: PromptCreateJobFn,
    createUserJobsTabViewModel: UserJobsTabViewModelFactory,
    createDocumentsTabViewModel: DocumentsTabViewModelFactory,
    createClientAssignmentViewModel: ClientAssignmentViewModelFactory,
    createAdvisorClientsTabViewModel: AdvisorClientsTabViewModelFactory,
    private readonly _canAccessUserManagement: () => boolean,
    readonly editUserRoleVm: EditUserRoleDialogViewModel,
    readonly restoreToWorkspaceVm: RestoreToWorkspaceDialogViewModel,
    readonly editProDetailsVm: EditProDetailsDialogViewModel,
    readonly manageSkillListVm: ManageSkillsListDialogViewModel,
    createRemoveFromWorkspaceDialogViewModel: CreateRemoveFromWorkspaceDialogViewModelFn,
    createManageUserCapacityDialogViewModel: CreateManageUserCapacityDialogViewModelFn,
    createPromoteToOwnerDialogViewModel: CreatePromoteToOwnerDialogViewModelFn,
    createAddUserToTeamDialogViewModel: CreateAddUserToTeamDialogViewModelFn,
    createAddUserToWorkspaceDialogViewModel: CreateAddUserToWorkspaceDialogViewModelFn,
    createAddCustomerToWorkspaceDialogViewModel: CreateAddCustomerToWorkspaceDialogViewModelFn,
    createJoinWorkspaceAsAdminDialogViewModel: CreateJoinWorkspaceAsAdminDialogViewModelFn,
    createEditUserInformationDialogViewModel: CreateEditUserInformationDialogViewModelFn,
    createMembershipsTabViewModel: CreateMembershipsTabViewModelFn,
  ) {
    this.promoteToOwnerVm = createPromoteToOwnerDialogViewModel(() =>
      this._refreshUser(),
    )

    this.removeFromWorkspaceVm = createRemoveFromWorkspaceDialogViewModel(() =>
      this._refreshInBackground(),
    )

    this.addUserToTeamVm = createAddUserToTeamDialogViewModel(() => this.user)

    this.addUserToWorkspaceVm = createAddUserToWorkspaceDialogViewModel(
      () => this.user,
      () => this._refreshUser(),
    )

    this.addCustomerToWorkspaceVm = createAddCustomerToWorkspaceDialogViewModel(
      () => this.user,
      () => this._refreshUser(),
    )

    this.joinWorkspaceAsAdminVm = createJoinWorkspaceAsAdminDialogViewModel(
      () => this.user.memberships.map((m) => m.workspace.id),
      () => this._refreshUser(),
    )

    this.editUserInformationVm = createEditUserInformationDialogViewModel(() =>
      this._refresh(),
    )

    this.manageUserCapacityVm = createManageUserCapacityDialogViewModel(() =>
      this._refreshInBackground(),
    )

    this.membershipsTabVm = createMembershipsTabViewModel(
      () => this.user,
      this,
      () => this.isViewingOwnProfileAndIsPlatformAdmin,
      () => this.isViewingOwnProfileAndIsPlatformAdmin,
    )

    this.jobsTabVm = createUserJobsTabViewModel({
      userPublicId: () => this.user.publicId,
      workspaces: () => this.availableWorkspaces,
    })

    this.documentsTabVm = createDocumentsTabViewModel({
      userPublicId: () => this.user.publicId,
      userMemberships: () => this.user.memberships,
      currentUserMemberships: () => this._getCurrentUserWorkspaceMemberships(),
    })

    this.clientAssignmentVm = createClientAssignmentViewModel({
      getUserPublicId: () => this.userPublicId,
      canHaveAssignment: () => this.user.userDataType === 'User',
    })

    this.clientsTabVm = createAdvisorClientsTabViewModel({
      getAdvisorUserPublicId: () => this.user.publicId,
    })

    // The memberships sidebar stays open across tabs, so the memberships tab
    // lives as long as the page rather than being swapped out with the others.
    this._lifecycle.manage(this.membershipsTabVm)
    this._lifecycle.manage(this.addUserToTeamVm)
    this._lifecycle.manage(this.addUserToWorkspaceVm)
    this._lifecycle.manage(this.clientAssignmentVm)

    // Tabs activate once the user has loaded, because they read the user.
    this._lifecycle.manage(
      toStream(() => (this.hasUser ? this.currentTab : null), true).pipe(
        filter((tab) => tab !== null),
        distinctUntilChanged(),
        switchMap((tab) => {
          if (tab === 'Memberships') {
            this.membershipsTabVm.refreshSelectedMembership()
            return EMPTY
          }

          return activation$(this._tabVm(tab))
        }),
      ),
    )
  }

  /**
   * Whether the user has loaded.
   */
  @computed
  get hasUser() {
    return this.user !== SearchableUser.none
  }

  @computed
  get isPlatformAdmin() {
    return this._getCurrentMember().user.platformAdmin
  }

  /**
   * Whether the current user is viewing their own profile and is a platform admin.
   */
  @computed
  get isViewingOwnProfileAndIsPlatformAdmin() {
    const currentUserPublicId = this._getCurrentMember().user.publicId
    const isViewingOwnProfile = this.user.publicId === currentUserPublicId
    return isViewingOwnProfile && this.isPlatformAdmin
  }

  /**
   * The Stripe account the Pro is paid out to, if connected.
   */
  @computed
  get payoutStripeAccountId() {
    return this.payoutMethodInfo.status === 'Connected'
      ? this.payoutMethodInfo.stripeAccountId
      : null
  }

  @computed
  get isUserDataTypePro() {
    return this.user.userDataType === 'Pro'
  }

  @computed
  get availableWorkspacesToManageBilling() {
    return this._getCurrentUserWorkspaceMemberships()
      .filter((mb) => mb.member.permissions.adminBillingManage)
      .map((mb) => mb.workspace)
  }

  /**
   * The list of workspaces available to the current user (i.e. the admin).
   */
  @computed
  get currentUserWorkspaces() {
    return this._getCurrentUserWorkspaceMemberships()
      .filter((mb) => mb.member.permissions.adminUserModify)
      .map((mb) => mb.workspace)
  }

  /**
   * The list of workspaces that are available to the currently selected user
   * AND that the current user is a member of.
   */
  @computed
  get availableWorkspaces() {
    return this.user.memberships
      .map((mb) => mb.workspace)
      .filter((ws) => this.currentUserWorkspaces.includes(ws))
  }

  /**
   * The list of ACTIVE workspace memberships that are available to the currently selected user
   * AND that the current user is a member of.
   */
  @computed
  get availableWorkspaceMemberships() {
    return this.user.memberships.filter(
      (mb) =>
        !mb.member.inactive &&
        this.currentUserWorkspaces.includes(mb.workspace),
    )
  }

  /**
   * The selected tab, or the first one when the selected tab is hidden.
   */
  @computed
  get currentTab(): UserDetailsTab {
    return this.tabs.includes(this.tab) ? this.tab : 'Jobs'
  }

  /**
   * The tabs shown for this user.
   */
  @computed
  get tabs(): UserDetailsTab[] {
    return userDetailsTabs.filter(
      (tab) =>
        tab !== 'Clients' || this.clientAssignmentVm.shouldShowClientsTab,
    )
  }

  /**
   * Whether the user should be allowed access to view user details.
   */
  @computed
  get canAccessUserDetails() {
    return this._canAccessUserManagement()
  }

  /**
   * Whether the current user can edit the selected user's information.
   */
  @computed
  get canEditUserInformation() {
    return this.availableWorkspaces.length > 0
  }

  /**
   * The list of workspaces that are available to the currently selected user
   * and that the current user can create a job for said user.
   */
  @computed
  get availableWorkspacesForJobCreation() {
    return this._getCurrentUserWorkspaceMemberships().filter(
      (m) =>
        m.member.permissions.adminJobCreate &&
        this.user.memberships.some((um) => um.workspace.id === m.workspace.id),
    )
  }

  /**
   * Whether the current user can create jobs for the selected user.
   */
  @computed
  get canCreateJobForUser() {
    return this.availableWorkspacesForJobCreation.length > 0
  }

  /**
   * Whether the current user can view the payout method for the selected user.
   */
  @computed
  get canViewPayoutMethod() {
    return this._getCurrentMember().user.platformAdmin
  }

  activate() {
    this._lifecycle.activate()
    if (this.hasUser) {
      this._fetchDetailsInBackground(this._lifecycle.signal)
    }
  }

  @action.bound
  deactivate() {
    this._lifecycle.deactivate()

    this.provider = Provider.none
    this.paymentMethods = []
    this.tab = 'Jobs'
    this.activeJobs = 0
    this.totalJobs = 0
    this.payoutMethodInfo = { status: 'Unknown' }
  }

  /**
   * Set the currently selected tab.
   */
  @action.bound
  setTab(tab: UserDetailsTab) {
    this.tab = tab
  }

  /**
   * Creates job for user.
   */
  @action.bound
  createJob() {
    if (!this.user.publicId) {
      return
    }

    this._promptCreateJob({
      ownerScopedInput: {
        publicId: this.user.publicId,
        workspaceIds: this.availableWorkspacesForJobCreation.map((m) =>
          Id.assert(m.workspace.id),
        ),
      },
    }).catch(() => undefined)
  }

  @action.bound
  editUserInformation() {
    this.editUserInformationVm.open(this.user, this.availableWorkspaces[0])
  }

  @action.bound
  editProDetails() {
    this.editProDetailsVm.open(this.user.publicId)
  }

  @action.bound
  editGlobalCapacity() {
    void this.manageUserCapacityVm.showWithUser(this.user, Workspace.none)
  }

  @action.bound
  showSoftwareOwned() {
    this.userInfoItemListVm.showWith({
      title: 'Software owned',
      userInfoItems: this.provider.softwareOwned,
    })
  }

  @action.bound
  showSoftwareExperience() {
    this.userInfoItemListVm.showWith({
      title: 'Software experience',
      userInfoItems: this.provider.softwareExperience,
    })
  }

  @action.bound
  showSpecialties() {
    this.userInfoItemListVm.showWith({
      title: 'Specialties',
      userInfoItems: this.provider.specialties,
    })
  }

  /**
   * Refetches the user, which includes their memberships.
   */
  private async _refreshUser() {
    const user = await this._fetchUser(
      this.user.publicId,
      this._lifecycle.signal,
    )
    runInAction(() => {
      this.user = user
    })
  }

  private async _fetchUser(userPublicId: Id, signal: AbortSignal) {
    const user = await this._fetchSearchableUser(
      Id.assert(userPublicId),
      { memberships: true },
      signal,
    )
    signal.throwIfAborted()
    return user
  }

  /**
   * Refetches the user and everything the page shows about them.
   */
  private async _refresh() {
    await this._refreshUser()
    await this._fetchDetails(this._lifecycle.signal)
  }

  private _refreshInBackground() {
    this._refresh().catch(() => undefined)
  }

  private _fetchDetailsInBackground(signal: AbortSignal) {
    this._fetchDetails(signal).catch(() => undefined)
  }

  private _tabVm(tab: Exclude<UserDetailsTab, 'Memberships'>) {
    switch (tab) {
      case 'Jobs':
        return this.jobsTabVm
      case 'Documents':
        return this.documentsTabVm
      case 'Clients':
        return this.clientsTabVm
    }
  }

  /**
   * Loads the job totals, billing and payout details for the user.
   */
  private async _fetchDetails(signal: AbortSignal) {
    const userPublicId = this.user.publicId

    const jobTotals = await this._fetchJobTotals(userPublicId, signal)
    signal.throwIfAborted()
    runInAction(() => {
      this.activeJobs = jobTotals.actives
      this.totalJobs = jobTotals.total
    })

    const billingProfiles = await Promise.all(
      this.availableWorkspaceMemberships
        .filter((mb) =>
          this.availableWorkspacesToManageBilling.some(
            (ws) => ws.id === mb.workspace.id,
          ),
        )
        .map((mb) => this._fetchBillingProfile(mb.member)),
    )
    signal.throwIfAborted()

    if (billingProfiles.length > 0) {
      runInAction(() => {
        this.paymentMethods = billingProfiles
          .map((profile) => profile.billingMethods.find((bm) => bm.isDefault))
          .filter((bm) => bm !== undefined)
      })
    }

    if (this.user.userDataType === 'Pro') {
      const provider = this._primeProvider({ userPublicId })
      if (provider) {
        runInAction(() => {
          this.provider = provider
        })
      }

      const payoutMethodInfo = await this._fetchPayoutMethodInfo(
        userPublicId,
        signal,
      )
      signal.throwIfAborted()
      runInAction(() => {
        this.payoutMethodInfo = payoutMethodInfo
      })
    }
  }
}
